import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCashRegister, faDesktop } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useAuth } from '../../contexts/AuthProvider';
import { ChefHatIcon } from '../PosShellPage/components/settings/ChefHatIcon';
import { requestAndRegisterFirebaseWebPush } from '../../services/push/firebase-push.service';
import classes from './backoffice-page.module.scss';

type BackofficePageProps = {
  tenantId: string;
};

type BackofficeOption = {
  key: 'pos' | 'kds' | 'dashboard';
  label: string;
  to: string;
  icon: IconProp | null;
};

type BeforeInstallPromptEventLike = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const detectPwaStandaloneMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const matchesDisplayMode = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return matchesDisplayMode || iosStandalone;
};

// Summary: Tenant-scoped backoffice home hub with role-based quick links.
// Details: docs/function-library/web/contexts-pages.md#pages-backofficepage
export const BackofficePage: React.FC<BackofficePageProps> = ({ tenantId }) => {
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role ?? 'user';
  const isMobile = useMediaQuery('(max-width: 540px)');
  const isAdminMobile = role === 'admin' && isMobile;
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isRunningInPwa, setIsRunningInPwa] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState<BeforeInstallPromptEventLike | null>(null);
  const actionInFlightRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updatePwaMode = () => {
      setIsRunningInPwa(detectPwaStandaloneMode());
    };

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredInstallPrompt(event as BeforeInstallPromptEventLike);
      updatePwaMode();
    };

    const onAppInstalled = () => {
      setDeferredInstallPrompt(null);
      updatePwaMode();
    };

    updatePwaMode();

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', onAppInstalled);

    const displayModeMedia = window.matchMedia?.('(display-mode: standalone)');
    const onDisplayModeChange = () => updatePwaMode();
    displayModeMedia?.addEventListener?.('change', onDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', onAppInstalled);
      displayModeMedia?.removeEventListener?.('change', onDisplayModeChange);
    };
  }, []);

  const handleInstallPwa = () => {
    if (!deferredInstallPrompt) {
      notifications.show({
        color: 'orange',
        title: 'לא ניתן לפתוח התקנה אוטומטית',
        message: 'פתח את תפריט הדפדפן ובחר "הוספה למסך הבית".',
        autoClose: 5000,
      });
      return;
    }

    void deferredInstallPrompt.prompt()
      .then(() => deferredInstallPrompt.userChoice)
      .then((choice) => {
        if (choice.outcome === 'accepted') {
          notifications.show({
            color: 'green',
            title: 'ההתקנה התחילה',
            message: 'אשר את ההתקנה כדי להשלים מעבר לאפליקציה.',
            autoClose: 3500,
          });
          return;
        }

        notifications.show({
          color: 'orange',
          title: 'ההתקנה בוטלה',
          message: 'ניתן לנסות שוב בכל רגע.',
          autoClose: 3500,
        });
      })
      .catch(() => {
        notifications.show({
          color: 'red',
          title: 'פתיחת התקנה נכשלה',
          message: 'לא הצלחנו לפתוח את חלון ההתקנה כרגע.',
          autoClose: 4000,
        });
      })
      .finally(() => {
        setDeferredInstallPrompt(null);
        actionInFlightRef.current = false;
        setIsActionLoading(false);
      });
  };

  const handleSubscribeUpdates = () => {
    void requestAndRegisterFirebaseWebPush(tenantId)
      .then((result) => {
        if (result.status === 'registered') {
          notifications.show({
            color: 'green',
            title: 'נרשמת בהצלחה',
            message: 'המכשיר נרשם לקבלת עדכונים.',
            autoClose: 3000,
          });
          return;
        }

        notifications.show({
          color: 'orange',
          title: 'לא ניתן להירשם לעדכונים',
          message: result.message,
          autoClose: 4000,
        });
      })
      .catch((error: unknown) => {
        notifications.show({
          color: 'red',
          title: 'הרישום נכשל',
          message: error instanceof Error ? error.message : 'שגיאה לא צפויה ברישום לעדכונים.',
          autoClose: 4000,
        });
      })
      .finally(() => {
        actionInFlightRef.current = false;
        setIsActionLoading(false);
      });
  };

  const handleHeaderAction = () => {
    if (actionInFlightRef.current) {
      return;
    }
    actionInFlightRef.current = true;
    setIsActionLoading(true);

    if (!isRunningInPwa) {
      handleInstallPwa();
      return;
    }

    handleSubscribeUpdates();
  };

  const actionButtonLabel = isRunningInPwa ? 'הירשם לקבלת עדכונים' : 'התקן אפליקציה';
  const actionButtonLoadingLabel = isRunningInPwa ? 'נרשם...' : 'פותח התקנה...';

  const options: BackofficeOption[] = [
    {
      key: 'pos',
      label: 'קופה',
      to: `/t/${tenantId}/pos`,
      icon: faCashRegister as IconProp,
    },
    {
      key: 'kds',
      label: 'מסך מטבח',
      to: `/t/${tenantId}/kds`,
      icon: null,
    },
    ...(role === 'admin' || role === 'super_admin'
      ? [
          {
            key: 'dashboard' as const,
            label: 'דשבורד',
            to: `/t/${tenantId}/dashboard`,
            icon: faDesktop as IconProp,
          },
        ]
      : []),
  ];

  return (
    <section className={classes.container} dir="rtl" aria-labelledby="backoffice-home-title">
      <div className={classes.card}>
        <div className={classes.titleBlock}>
          <div className={classes.titleRow}>
            <h1 id="backoffice-home-title" className={classes.title}>בית</h1>
            {isAdminMobile ? (
              <button
                type="button"
                className={classes.subscribeButton}
                onClick={handleHeaderAction}
                disabled={isActionLoading}
              >
                {isActionLoading ? actionButtonLoadingLabel : actionButtonLabel}
              </button>
            ) : null}
          </div>
          <p className={classes.subtitle}>בחר מסך עבודה</p>
        </div>
        <div className={classes.grid}>
          {options.map((option) => (
            <NavLink
              key={option.key}
              to={option.to}
              state={option.key === 'kds' ? { from: location.pathname } : undefined}
              className={classes.tile}
              data-testid={`backoffice-tile-${option.key}`}
            >
              <span className={classes.iconWrap}>
                {option.key === 'kds' ? (
                  <ChefHatIcon className={classes.icon} />
                ) : (
                  <FontAwesomeIcon icon={option.icon as IconProp} className={classes.icon} />
                )}
              </span>
              <span className={classes.label}>{option.label}</span>
            </NavLink>
          ))}
        </div>
      </div>
    </section>
  );
};
